import type { App as AppManifest, BlockDef } from '@cogo/types-v2'
import type {
  ChannelOutput,
  ComposeCandidateSummary,
  RenderedProvenance,
} from '../_shared/channel-types'
import type { BlockSpec } from './intent-to-blockspec'
import {
  clamp01,
  lastSegment,
  runtimeTargetsForText,
  summarizeBlockBindings,
  unique,
} from './compose-preview'

/**
 * Subset of cogo-cloudflare /api/v3/block/generate response we consume.
 * Full route shape: cogo-platform/apps/cogo-cloudflare/src/routes/block-v3.ts
 *
 * Phase 1 only depends on the documented success/failure surface; we don't
 * type the full backend response (it has additional debug/telemetry fields
 * that are not part of the Channel 2 contract).
 */
export interface BackendBlockBundle {
  meta?: Record<string, unknown>
  ui?: Record<string, unknown>
  events?: Record<string, unknown>
  flow?: Record<string, unknown>
  state?: Record<string, unknown>
  data?: Record<string, unknown>
}

/**
 * Backend response shape varies by pipeline mode (block-v3.ts):
 *   - hybrid path        → `{ files: {...}, validation, quality, pipeline: 'hybrid' }`
 *   - v2-explicit path   → `{ meta, ui, events, flow, state, data, pipeline: 'v2-explicit' }`
 *                          (block-v3.ts:112 spreads `v2Out` at top level — no `files` wrapper)
 *   - v2-fallback path   → `{ meta, ui, events, flow, state, data, pipeline: 'v2-fallback', fallbackReason }`
 *
 * `extractBundle()` normalizes both — returns `body.files` if present, else
 * synthesizes a bundle from any of the 6 file keys spread at top level.
 *
 * Discovered during P1 dogfood (2026-04-28): `pipeline:'v2'` request returns
 * the v2-explicit shape; my P1.1 audit only covered the hybrid wrapper.
 */
/**
 * Per-candidate selection facts measured by the backend picker at pick time
 * (cogo-platform `RetrievalPickTrace` — compose provenance). Every field is
 * something the picker actually computed; the client renders but never
 * invents these.
 */
export interface BackendRetrievalCandidate {
  id: string
  /** Corpus admission score (cogo:score, typically 0–100). */
  score?: number
  /** cogo:qualityGrade (0–100) — rendered design quality. */
  qualityGrade?: number
  semanticType?: string
  /** R3 intent↔block token-overlap count. */
  intentMatchCount?: number
  /** R3a canonical-exemplar keyword hits. */
  canonicalMatchCount?: number
  hasBody?: boolean
  /** True for the candidate the backend picker actually served. */
  picked?: boolean
}

export interface BackendRetrievalTrace {
  pickerMode?: 'deterministic' | 'llm'
  consideredCount?: number
  intentTokenCount?: number
  candidates?: BackendRetrievalCandidate[]
  anchor?: { semanticType?: string; pattern?: string; dsId?: string }
  queryFallback?: 'semantic-type-only'
}

export interface BackendV3SuccessResponse {
  files?: BackendBlockBundle
  // v2-explicit / v2-fallback: bundle keys live at top level (no `files`).
  meta?: BackendBlockBundle['meta']
  ui?: BackendBlockBundle['ui']
  events?: BackendBlockBundle['events']
  flow?: BackendBlockBundle['flow']
  state?: BackendBlockBundle['state']
  data?: BackendBlockBundle['data']
  validation?: {
    valid: boolean
    errors?: Array<{ rule?: string; message?: string }>
    warnings?: Array<{ rule?: string; message?: string }>
    issues?: Array<{ rule?: string; message?: string }>
  }
  quality?: { score: number; grade: 'pass' | 'warn' | 'reject' }
  pipeline?: 'hybrid' | 'v2-explicit' | 'v2-fallback'
  fallbackReason?: { stage?: string; lastDiagnostic?: unknown }
  /** Retrieve+bind selection trace — present only when retrieval served the
   *  Phase-1 UI AND the deployed backend emits it (additive field). */
  retrieval?: BackendRetrievalTrace
}

export interface BackendV3FailureResponse {
  error: string
  failedStage?: string
  validation?: BackendV3SuccessResponse['validation']
  pipeline?: 'both-failed'
}

export type BackendV3Response = BackendV3SuccessResponse | BackendV3FailureResponse

const BUNDLE_KEYS = ['meta', 'ui', 'events', 'flow', 'state', 'data'] as const

/**
 * Normalize the response body to a BackendBlockBundle.
 *   - hybrid: `body.files` (nested wrapper)
 *   - v2-*  : top-level spread (any of meta/ui/events/flow/state/data present)
 *
 * Returns null only when neither shape is detectable — caller surfaces this
 * as `miss/backend-failed` with details.
 */
function extractBundle(
  body: BackendV3SuccessResponse,
): BackendBlockBundle | null {
  if (body.files && typeof body.files === 'object') {
    return body.files
  }
  if (BUNDLE_KEYS.some((k) => k in body && (body as Record<string, unknown>)[k] !== undefined)) {
    return {
      meta: body.meta,
      ui: body.ui,
      events: body.events,
      flow: body.flow,
      state: body.state,
      data: body.data,
    }
  }
  return null
}

/**
 * Lift the backend's generation provenance off block meta
 * (`meta.generationSource` / `meta.exampleId` — cogo-platform review C3/D-2).
 * Returns undefined when the deployed backend predates the provenance stamp,
 * so older responses simply render without a provenance claim.
 */
export function extractProvenance(
  bundle: BackendBlockBundle | null,
): RenderedProvenance | undefined {
  const meta = bundle?.meta
  if (!meta || typeof meta !== 'object') return undefined
  const source = (meta as Record<string, unknown>).generationSource
  if (source !== 'retrieval' && source !== 'zero-shot') return undefined
  const exampleId = (meta as Record<string, unknown>).exampleId
  return {
    generationSource: source,
    ...(typeof exampleId === 'string' && exampleId ? { exampleId } : {}),
  }
}

/** Corpus scores arrive on the 0–100 admission scale; the card model (and the
 *  mock pipeline it shares a renderer with) uses 0..1. */
function normalizeTraceScore(score: number | undefined): number {
  if (typeof score !== 'number' || !Number.isFinite(score)) return 0
  return clamp01(score > 1 ? score / 100 : score)
}

/** v2 bundle FILES nest their payload one level (`state: {state: {...}}`,
 *  `flow: {flows: {...}}`, `events: {bindings: {...}}`); the binding summary
 *  collects direct keys, so unwrap when the nested key is present. */
function unwrapNested(
  value: Record<string, unknown> | undefined,
  key: string,
): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object') return value
  const nested = value[key]
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return value
}

/**
 * Map the backend's retrieve+bind pick trace to the compose-preview card the
 * mock pipeline already renders ("why this block was selected").
 *
 * Honesty contract (mirrors the campaign provenance rules):
 *   - Candidate facts come ONLY from the backend trace — measured at pick time.
 *   - The picked candidate's binding summary is computed from the ACTUAL
 *     returned bundle; unpicked candidates were never bound, so their binding
 *     lists stay empty and their evidence reads `missing`.
 *   - Returns null when the response carries no usable trace (zero-shot, older
 *     deployed backend, malformed trace) — the caller then emits NO card
 *     rather than a forged one.
 */
export function backendRetrievalToComposePreview(
  success: BackendV3SuccessResponse,
  blockSpec: BlockSpec,
): Extract<ChannelOutput, { kind: 'compose-preview' }> | null {
  const trace = success.retrieval
  const rawCandidates = Array.isArray(trace?.candidates) ? trace.candidates : []
  const traced = rawCandidates.filter(
    (c): c is BackendRetrievalCandidate =>
      !!c && typeof c === 'object' && typeof c.id === 'string' && c.id.length > 0,
  )
  if (!trace || traced.length === 0) return null

  // The served winner leads the list so the card (top-4 visible) always shows
  // it; the rest keep the backend's best-first display order.
  const picked = traced.filter((c) => c.picked === true)
  if (picked.length !== 1) return null
  const ordered = [picked[0], ...traced.filter((c) => c.picked !== true)]

  // Same BlockDef shape the mock pipeline summarizes (boundBlock), with the
  // v2 file-nesting unwrapped so direct-key collection sees the real names.
  const bundle = extractBundle(success)
  const pickedBindings = bundle
    ? summarizeBlockBindings({
        ...bundleToBlockDef(bundle, blockSpec.name),
        state: unwrapNested(bundle.state, 'state'),
        flow: unwrapNested(bundle.flow, 'flows'),
        events: unwrapNested(bundle.events, 'bindings'),
      } as unknown)
    : { requiredState: [], requiredActions: [], requiredDatasources: [] }
  const warnings = (success.validation?.warnings ?? [])
    .map((w) => w.message ?? '(no-message)')
  const intentTokenCount = trace.intentTokenCount ?? 0

  const candidates: ComposeCandidateSummary[] = ordered.map((c) => {
    const isPicked = c.picked === true
    return {
      id: c.id,
      semanticType: lastSegment(c.semanticType ?? c.id),
      patternRef: trace.anchor?.pattern ?? c.id,
      intentMatch:
        intentTokenCount > 0 ? clamp01((c.intentMatchCount ?? 0) / intentTokenCount) : 0,
      // Raw token counts so the card can render "intent 2/14" — the ratio is
      // a measured overlap, not a quality score, and a "14%" badge invited
      // reading it as one (2026-06-11 dogfood).
      ...(intentTokenCount > 0
        ? { intentMatchDetail: { matched: c.intentMatchCount ?? 0, total: intentTokenCount } }
        : {}),
      ...(typeof c.qualityGrade === 'number' ? { qualityGrade: c.qualityGrade } : {}),
      score: normalizeTraceScore(c.score),
      runtimeTargets: runtimeTargetsForText(c.semanticType ?? ''),
      ...(isPicked
        ? pickedBindings
        : { requiredState: [], requiredActions: [], requiredDatasources: [] }),
      // Unpicked candidates are never bound — their evidence is simply not
      // collected ('unbound'), not looked-for-and-absent ('missing').
      evidenceState: isPicked ? (warnings.length > 0 ? 'partial' : 'complete') : 'unbound',
      status: 'ready',
    }
  })

  const bindPreview = {
    state: pickedBindings.requiredState,
    actions: pickedBindings.requiredActions,
    datasources: pickedBindings.requiredDatasources,
    warnings: unique(warnings, 5),
  }

  return {
    kind: 'compose-preview',
    plan: {
      intent: blockSpec.description || blockSpec.name,
      pageCount: 1,
      // True retrieval size (the trace embeds a capped subset of it)…
      candidateCount: trace.consideredCount ?? candidates.length,
      // …but only the embedded candidates are claimed ready: the backend
      // measured them; the rest were considered, not vouched for.
      readyCandidateCount: candidates.length,
      blockedCandidateCount: 0,
      stateBindingCount: bindPreview.state.length,
      actionBindingCount: bindPreview.actions.length,
      datasourceBindingCount: bindPreview.datasources.length,
    },
    candidates,
    bindPreview,
  }
}

/**
 * Convert backend `files` shape to AppV2 `BlockDef`.
 *
 * AppRuntime reads `block.ui.root` (`@cogo/react-renderer-v2` `app-runtime.ts:519`).
 * Backend `pr2ResultToBundle` returns `ui` as `Record<string, unknown>`. The
 * concrete shape depends on whether the LLM produced a wrapped tree or a bare
 * UiElement — handle both. This mirrors A3.1 channel-1 dispatch.ts:158 (lesson
 * F-1, 2026-04-28).
 */
export function bundleToBlockDef(
  files: BackendBlockBundle,
  blockName: string,
): BlockDef {
  const rawUi = files.ui ?? {}
  const ui =
    'root' in rawUi
      ? (rawUi as { root: unknown })
      : { root: rawUi }

  return {
    meta: (files.meta ?? { id: blockName, name: blockName }) as never,
    ui: ui as never,
    events: (files.events ?? {}) as never,
    flow: (files.flow ?? {}) as never,
    state: (files.state ?? {}) as never,
    data: (files.data ?? {}) as never,
  }
}

/**
 * Synthesize a single-page AppManifest containing exactly one block. Mirrors
 * channel-1/dispatch.ts:139 and channel-2/mock-stream.ts:137 (lessons F-1
 * and F-5: AppRuntime requires `{id, route, layout, blocks, blockLayout}`
 * and `layout: 'stack'` avoids syncNav console noise).
 */
export function synthesizeManifestForSingleBlock(
  blockName: string,
): AppManifest {
  return {
    rootBlockId: blockName,
    pages: [
      {
        id: 'home',
        route: '/',
        layout: 'stack',
        blocks: [blockName],
        blockLayout: {},
      },
    ],
  } as never
}

/**
 * Compact 8-char hex shape SHA — uses FNV-1a (mirrors mock-corpus-picker
 * `hashIntent` in form, not crypto). Distinguishes real-mode dispatches in
 * logs/telemetry from mock-mode `mock-XXXXXXXX` and channel-1 SHAs.
 */
function realModeShapeSha(blockName: string, pipeline: string | undefined): string {
  const seed = `${pipeline ?? 'unknown'}:${blockName}`
  let h = 0x811c9dc5 // FNV offset basis
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return `real-${(h >>> 0).toString(16).padStart(8, '0').slice(-8)}`
}

export interface MapBackendOptions {
  /** Studio-level timing of the dispatch (fetch start → parse). */
  startMs: number
}

/**
 * Translate a backend response (or fetch error) into a single ChannelOutput.
 *
 * Mapping:
 *   - 2xx + quality.grade in {pass, warn} → `rendered` (channel: 2)
 *   - 2xx + quality.grade = 'reject'      → `shaclFail` (production gate)
 *   - 2xx + validation.valid = false      → `shaclFail`
 *   - non-2xx + validation present        → `shaclFail` with diagnostics
 *   - non-2xx without validation          → `miss` reason='backend-failed'
 *
 * Aborted dispatches are surfaced as `miss` reason='aborted' BEFORE this
 * function is called (the fetch wrapper catches AbortError); we don't
 * inspect the response here for abort.
 */
export function backendV3ToChannelOutput(
  res: { ok: boolean; status: number; body: BackendV3Response },
  blockSpec: BlockSpec,
  opts: MapBackendOptions,
): ChannelOutput {
  const totalMs = Date.now() - opts.startMs
  const success = res.body as BackendV3SuccessResponse
  const failure = res.body as BackendV3FailureResponse

  // Non-2xx
  if (!res.ok) {
    const validation = (success.validation ?? failure.validation) as
      | BackendV3SuccessResponse['validation']
      | undefined
    if (validation && validation.valid === false) {
      const errs = validation.errors ?? validation.issues ?? []
      return {
        kind: 'shaclFail',
        diagnostics: errs.map(
          (e) => `${e.rule ?? '(no-rule)'}: ${e.message ?? '(no-message)'}`,
        ),
      }
    }
    return {
      kind: 'miss',
      reason: 'backend-failed',
      details: `${res.status}: ${failure.error ?? 'backend returned non-2xx'}`,
    }
  }

  // 2xx — check quality gate
  if (success.quality?.grade === 'reject') {
    const issues = success.validation?.issues ?? success.validation?.errors ?? []
    return {
      kind: 'shaclFail',
      diagnostics: issues.map(
        (i) => `${i.rule ?? '(no-rule)'}: ${i.message ?? '(no-message)'}`,
      ),
    }
  }

  // 2xx + validation.valid === false (defensive — backend usually 5xx in this case)
  if (success.validation?.valid === false) {
    const errs = success.validation.errors ?? success.validation.issues ?? []
    return {
      kind: 'shaclFail',
      diagnostics: errs.map(
        (e) => `${e.rule ?? '(no-rule)'}: ${e.message ?? '(no-message)'}`,
      ),
    }
  }

  // Happy path — bundle may be nested under `files` (hybrid) OR spread at top
  // level (v2-explicit / v2-fallback). extractBundle() normalizes both.
  const bundle = extractBundle(success)
  if (!bundle) {
    return {
      kind: 'miss',
      reason: 'backend-failed',
      details: '2xx response has neither `files` nor any top-level bundle keys',
    }
  }

  const blockDef = bundleToBlockDef(bundle, blockSpec.name)
  const manifest = synthesizeManifestForSingleBlock(blockSpec.name)
  const bindWarnings = (success.validation?.warnings ?? []).map(
    (w) => w.message ?? '(no-message)',
  )
  const provenance = extractProvenance(bundle)

  return {
    kind: 'rendered',
    channel: 2,
    manifest,
    blocks: { [blockSpec.name]: blockDef },
    bindWarnings,
    timings: {
      lookupMs: 0,
      bindMs: 0,
      generationMs: totalMs,
      totalMs,
    },
    shapeSha: realModeShapeSha(blockSpec.name, success.pipeline),
    ...(provenance ? { provenance } : {}),
  }
}
